import type { PlaybookId } from "@repo/app-types/entities/playbook";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import type { FC } from "react";
import { useAllMinimalPlaybooksQuery } from "../../../../api/entities/playbook.js";
import { Can } from "../../../../helpers/teammateAuthorizationContext.js";
import { useLearningJourneyPlaybookId } from "../../../courses/learningJourneyPlaybookHooks.js";

interface PlaybookSettingProps {
  playbookId: PlaybookId | null;
  onSave: (playbookId: PlaybookId | null) => void;
  allowsEmptySelection?: boolean;
  usesEffectivePlaybookId?: boolean;
  description?: string;
}

export const PlaybookSetting: FC<PlaybookSettingProps> = ({
  playbookId,
  onSave,
  allowsEmptySelection = true,
  usesEffectivePlaybookId = true,
  description,
}) => {
  const { data: playbooks, isLoading } = useAllMinimalPlaybooksQuery({
    shouldExcludeTeammateAgentPlaybooks: true,
  });

  const options: SimpleSelectOption<PlaybookId | null>[] =
    !playbooks?.length && allowsEmptySelection
      ? [
          {
            value: null,
            label: "None",
          },
        ]
      : [
          ...(allowsEmptySelection
            ? [
                {
                  value: null,
                  label: "None",
                },
              ]
            : []),
          ...(playbooks?.map((playbook) => ({
            value: playbook.id,
            label: playbook.name,
          })) ?? []),
        ];

  const { playbookId: effectivePlaybookId } =
    useLearningJourneyPlaybookId(playbookId);

  const value = usesEffectivePlaybookId ? effectivePlaybookId : playbookId;

  return (
    <Can
      I={PermissionActions.UPDATE}
      a={PermissionSubjects.JOB_OPENINGS}
      passThrough={true}
    >
      {(allowed) => (
        <div className="space-y-2">
          <Label size={Size.SMALL} className="block">
            Playbook
          </Label>
          <div>
            <SimpleSelect
              options={options}
              value={value}
              onChange={onSave}
              isDisabled={!allowed || isLoading}
              placeholder="Select a playbook"
            />
          </div>
          <p className="text-sm text-muted-foreground">
            {description ??
              "Assess participants for their ability to succeed in executing relevant parts of your playbook."}
          </p>
        </div>
      )}
    </Can>
  );
};
